"use client";

import Link from "next/link";

/**
 * One selectable row inside a /jobs filter dropdown (facet value or toggle).
 *
 * A CLIENT component on purpose (CZ-635). The rows only render inside an open
 * FilterDropdown, so the server never puts them in the HTML — it serialises
 * them into the RSC payload instead. As a server component each row shipped its
 * whole element tree and four long className strings (~900 bytes); /jobs?q=
 * carried ~1,550 rows = 1.4 MB of payload for 110 KB of markup. Returning
 * visitors (JS cached) then hydrated while that stream was still arriving,
 * which is where the Chrome #418 hydration errors came from. As a client
 * component a row is just its five props.
 */
export function ToggleRow({
  label,
  count,
  selected,
  href,
  title,
  hint,
  accent = false,
  onClick,
}: {
  label: string;
  count?: number;
  selected: boolean;
  href: string;
  title?: string;
  /** Small muted suffix after the label (a city's country code). */
  hint?: string;
  /** Tint the row — the Location panel's browsed region. */
  accent?: boolean;
  onClick?: () => void;
}) {
  return (
    <li>
      <Link
        href={href}
        scroll={false}
        rel="nofollow"
        title={title}
        onClick={onClick}
        className={`group flex items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-neutral-100 ${accent ? "bg-neutral-100" : ""} ${
          selected ? "font-medium text-neutral-900" : "text-neutral-600 hover:text-neutral-900"
        }`}
      >
        <span
          aria-hidden
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] leading-none ${
            selected
              ? "border-brand-600 bg-brand-600 text-white"
              : "border-neutral-300 bg-white text-transparent group-hover:border-neutral-400"
          }`}
        >
          ✓
        </span>
        <span className="min-w-0 flex-1 truncate">
          {label}
          {hint ? <span className="ml-1.5 text-[11px] font-normal text-neutral-400">{hint}</span> : null}
        </span>
        {count != null ? (
          <span className="shrink-0 text-xs tabular-nums text-neutral-400">
            {count.toLocaleString("en-US")}
          </span>
        ) : null}
      </Link>
    </li>
  );
}
